import {fetchPoll} from '@github-ui/fetch-utils'
import {changeIssuesTabCounter} from '@github-ui/issues-tab-counter'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type RepoIssuesBulkUpdate =
  | Readonly<{state: 'OPEN'; stateReason?: never}>
  | Readonly<{state: 'CLOSED'; stateReason: 'COMPLETED' | 'NOT_PLANNED'}>
  | Readonly<{addedLabelIds: readonly string[]; removedLabelIds: readonly string[]}>
  | Readonly<{applyAssigneeIds: readonly string[]; removeAssigneeIds: readonly string[]}>
  | Readonly<{addToProjectIds: readonly string[]; removeFromProjectIds: readonly string[]}>
  | Readonly<{milestoneId: string}>
  | Readonly<{clearMilestone: true}>
  | Readonly<{issueTypeId: string}>
  | Readonly<{clearIssueType: true}>

export type RepoIssuesBulkTarget = Readonly<{ids: readonly string[]}> | Readonly<{query: string; repositoryId: string}>

export interface RepoIssuesBulkUpdateResult {
  failedIssueNodeIds: string[]
}

export interface RepoIssuesBulkUpdateProgress {
  state: 'PENDING' | 'QUEUED' | 'STARTED'
  percentage: number
  failedCount: number
}

export const REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE =
  'The update timed out, but some changes might have been applied. Refresh the page before trying again.'

const REPO_ISSUES_BULK_UPDATE_TIMEOUT_MS = 5 * 60 * 1000

type IssueForTabCounter = Pick<IssueListItem, 'globalId' | 'id' | 'itemType' | 'state'>

export function updateIssuesTabCounter({
  issues,
  update,
  failedIssueIds,
  allMatching,
}: {
  issues: IssueForTabCounter[]
  update: RepoIssuesBulkUpdate
  failedIssueIds: ReadonlySet<string>
  allMatching: boolean
}): void {
  let delta = 0
  if (!allMatching && 'state' in update) {
    for (const issue of issues) {
      if (failedIssueIds.has(getListItemIdentity(issue))) continue
      if (issue.state === 'OPEN' && update.state === 'CLOSED') delta -= 1
      else if (issue.state === 'CLOSED' && update.state === 'OPEN') delta += 1
    }
  }
  if (delta !== 0) changeIssuesTabCounter(delta)
}

function mutationJobId(payload: unknown): string {
  if (!payload || typeof payload !== 'object') throw new Error('Bulk update returned an invalid response')
  if ('errors' in payload && Array.isArray(payload.errors) && payload.errors.length > 0) {
    const firstError = payload.errors[0]
    throw new Error(
      firstError && typeof firstError === 'object' && 'message' in firstError && typeof firstError.message === 'string'
        ? firstError.message
        : 'Bulk update failed',
    )
  }

  if (!('data' in payload) || !payload.data || typeof payload.data !== 'object') {
    throw new Error('Bulk update returned an invalid response')
  }
  const data = payload.data as Record<string, unknown>
  const mutation = data.updateIssuesBulk ?? data.updateIssuesBulkByQuery
  if (!mutation || typeof mutation !== 'object' || !('jobId' in mutation) || typeof mutation.jobId !== 'string') {
    throw new Error('Bulk update did not return a job ID')
  }
  return mutation.jobId
}

interface ExecutionError {
  node_id: string
  message: string
}

function isExecutionError(value: unknown): value is ExecutionError {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'node_id' in value &&
    typeof value.node_id === 'string' &&
    'message' in value &&
    typeof value.message === 'string',
  )
}

function jobFromPayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || !('job' in payload)) {
    throw new Error('Bulk update returned an invalid job response')
  }
  const job = payload.job
  if (!job || typeof job !== 'object') {
    throw new Error('Bulk update returned an invalid job response')
  }
  return job as Record<string, unknown>
}

function failedIssueNodeIds(payload: unknown, submittedIds?: ReadonlySet<string>): string[] {
  const job = jobFromPayload(payload)
  if (!Array.isArray(job.execution_errors)) throw new Error('Bulk update returned an invalid job response')

  const failedIds = new Set<string>()
  for (const executionError of job.execution_errors) {
    if (!isExecutionError(executionError)) {
      throw new Error('Bulk update returned an invalid job response')
    }
    if (submittedIds && !submittedIds.has(executionError.node_id)) {
      throw new Error('Bulk update returned an unexpected failed issue')
    }
    failedIds.add(executionError.node_id)
  }
  return [...failedIds]
}

function jobProgress(payload: unknown): RepoIssuesBulkUpdateProgress {
  const job = jobFromPayload(payload)
  const state = typeof job.state === 'string' ? job.state.toUpperCase() : ''
  if (state !== 'PENDING' && state !== 'QUEUED' && state !== 'STARTED') {
    throw new Error('Bulk update returned an invalid job response')
  }
  return {
    state,
    percentage: typeof job.percentage === 'number' ? job.percentage : 0,
    failedCount: Array.isArray(job.execution_errors) ? job.execution_errors.length : 0,
  }
}

export async function updateIssues({
  target,
  update,
  onProgress,
}: {
  target: RepoIssuesBulkTarget
  update: RepoIssuesBulkUpdate
  onProgress?: (progress: RepoIssuesBulkUpdateProgress) => void
}): Promise<RepoIssuesBulkUpdateResult> {
  if ('ids' in target && (target.ids.length < 1 || target.ids.length > 1000)) {
    throw new Error('Select between 1 and 1,000 issues')
  }

  const abortController = new AbortController()
  const timeout = setTimeout(() => abortController.abort(), REPO_ISSUES_BULK_UPDATE_TIMEOUT_MS)

  try {
    const response = await reactFetchJSON('/_ghui/item-pickers/bulk_actions', {
      method: 'POST',
      body: {...target, ...update},
      signal: abortController.signal,
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const jobId = mutationJobId(await response.json())
    onProgress?.({state: 'PENDING', percentage: 0, failedCount: 0})
    const jobUrl = new URL(`/_jobs/${encodeURIComponent(jobId)}`, ssrSafeLocation.origin)
    if (jobUrl.origin !== ssrSafeLocation.origin) throw new Error('Bulk update returned a cross-origin job URL')
    const jobResponse = await fetchPoll(`${jobUrl.pathname}${jobUrl.search}`, {
      headers: {Accept: 'application/json'},
      signal: abortController.signal,
      onPoll: async progressResponse => onProgress?.(jobProgress(await progressResponse.json())),
    })

    return {
      failedIssueNodeIds: failedIssueNodeIds(
        await jobResponse.json(),
        'ids' in target ? new Set(target.ids) : undefined,
      ),
    }
  } catch (error) {
    if (abortController.signal.aborted) throw new Error(REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE)
    throw error
  } finally {
    clearTimeout(timeout)
  }
}
